import { useCallback, useMemo } from "react"
import { useField } from "formik"
import { EventClickArg } from "@fullcalendar/core"
import { enqueueSnackbar } from "notistack"

import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { ResourceDetailsDialog } from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"
import { fetchFreeBusyScheduleForServiceCenter, FreeBusyScheduleResponse } from "../../../createOrderApi"
import { ResourceItem } from "../../../createOrderTypes"

import { StandardTimelineCalendar } from "../calendar/StandardTimelineCalendar"
import { generateCalendarEvents, toFloatingIsoMinutes } from "../calendar/helpers"
import { ResourceCalendarGroupContent } from "./ResourceCalendarGroupContent"
import { ResourceCalendarLabelContent } from "./ResourceCalendarLabelContent"
import { ResourceBookingPreviewImage } from "./ResourceBookingPreviewImage"
import { usePreviewImageDialog } from "./usePreviewImageDialog"
import { useResourceDetailsDialog } from "./useResourceDetailsDialog"
import { createNewEvent, getBookingRangeFromAvailabilitySlot } from "./helpers"
import {
    NullableCalendarEvent,
    TimelineCalendarEvent,
    TimelineCalendarResource,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
} from "../calendar/fullCalendarTypes"

const INTERVAL_MINUTES = 15

const transformData = (data: FreeBusyScheduleResponse) => {
    return data?.resources || []
}

type WeeklyResourceBookingCalendarProps = {
    timelineResources: TimelineCalendarResource[]
    searchRegex: RegExp | undefined
    groupImagesMap: Map<string, string>
    handleBookClick: () => void
}

export function WeeklyResourceBookingCalendar({
    timelineResources,
    searchRegex,
    groupImagesMap,
    handleBookClick,
}: WeeklyResourceBookingCalendarProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { selectedImage, openImageModal, closeImageModal } = usePreviewImageDialog()
    const { resourceDetails, openResourceDetailsDialog, closeResourceDetailsDialog } = useResourceDetailsDialog()

    const [, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")
    const { mutateAsync: callEventsMutation } = useApiMutation(fetchFreeBusyScheduleForServiceCenter, {})

    const resourceUidToCalendarResourceId = useMemo(
        () => new Map(timelineResources.map((r) => [r.uid, r.id])),
        [timelineResources],
    )

    const createEventSourceFetcher = useCallback(
        (serviceCenterUid: string) =>
            async (
                fetchInfo: { startStr: string; endStr: string },
                successCallback: (events: TimelineCalendarEvent[]) => void,
                failureCallback: (error: any) => void,
            ) => {
                try {
                    const result = await callEventsMutation({
                        serviceCenterUid,
                        availabilityViewInterval: INTERVAL_MINUTES,
                        startTime: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromISO(fetchInfo.startStr)),
                        endTime: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromISO(fetchInfo.endStr)),
                    })

                    const slots = transformData(result).filter((slot) =>
                        resourceUidToCalendarResourceId.has(slot.resource.uid),
                    )
                    const events = generateCalendarEvents(
                        slots,
                        INTERVAL_MINUTES,
                        dateTimeFactory,
                        resourceUidToCalendarResourceId,
                    )

                    successCallback(events)
                } catch (err) {
                    failureCallback(err)
                }
            },
        [callEventsMutation, dateTimeFactory, resourceUidToCalendarResourceId],
    )

    const eventSources = useMemo(() => {
        const uniqueServiceCenterUidList = [...new Set(timelineResources.map((r) => r.serviceCenterUid))]

        return uniqueServiceCenterUidList.map((uid) => ({
            id: `source-${uid}`,
            events: createEventSourceFetcher(uid),
        }))
    }, [timelineResources, createEventSourceFetcher])

    const handleChangeLocalEvent = (event: NullableCalendarEvent) => {
        eventHelpers.setValue(event)

        if (!event) {
            fullResourcesHelpers.setValue([])
            return
        }
        const fromTimeline = timelineResources
            .filter((res) => event.resourceIds.includes(res.id))
            .map((res) => ({
                ...res,
                id: Number(res.id),
            }))
        fullResourcesHelpers.setValue(fromTimeline)
    }

    const handleEventClick = (info: EventClickArg) => {
        const event = info.event
        const resourceId = event._def.resourceIds?.[0]

        if (!resourceId) return

        const slotStart = dateTimeFactory.fromISO(event.startStr)
        const slotEnd = dateTimeFactory.fromISO(event.endStr)
        const now = dateTimeFactory.now()

        const bookingRange = getBookingRangeFromAvailabilitySlot({
            dateTimeFactory,
            slotStart,
            slotEnd,
            now,
            intervalMinutes: INTERVAL_MINUTES,
            allDayStartHour: 8,
            maxDurationHours: 1,
        })

        if (!bookingRange) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-past-booking-attempt-message"),
            })
            return
        }

        const { effectiveStart, adjustedEnd, originalEnd } = bookingRange

        const start = toFloatingIsoMinutes(dateTimeFactory, effectiveStart)
        const end = toFloatingIsoMinutes(dateTimeFactory, adjustedEnd)
        const timeRange = { start, end: toFloatingIsoMinutes(dateTimeFactory, originalEnd) }

        handleChangeLocalEvent(createNewEvent(resourceId, start, end, timeRange))
        handleBookClick()
    }

    const resourceLabelContent = (resourceContent: TimelineResourceLabelContent) => (
        <ResourceCalendarLabelContent
            resourceContent={resourceContent}
            searchRegex={searchRegex}
            handleOpenDetails={openResourceDetailsDialog}
        />
    )

    const resourceGroupLabelContent = (resourceContent: TimelineResourceGroupLabelContent) => (
        <ResourceCalendarGroupContent
            resourceContent={resourceContent}
            groupImagesMap={groupImagesMap}
            onImageClick={openImageModal}
        />
    )

    return (
        <>
            <StandardTimelineCalendar
                viewMode="weekGrid"
                eventSources={eventSources}
                resources={timelineResources}
                resourceLabelContent={resourceLabelContent}
                resourceGroupLabelContent={resourceGroupLabelContent}
                handleEventClick={handleEventClick}
            />
            {selectedImage && (
                <ResourceBookingPreviewImage previewImage={selectedImage} handleClose={closeImageModal} />
            )}
            {resourceDetails && (
                <ResourceDetailsDialog resourceDetails={resourceDetails} handleClose={closeResourceDetailsDialog} />
            )}
        </>
    )
}
